import { useEffect, useState } from 'react';
import { lookupsAPI } from '../services/api';
import { LookupItem } from '../types';

const CACHE_TTL_MS = 0; // Always refresh on mount while sharing in-flight requests

let cachedTypes: LookupItem[] | null = null;
let cacheTimestamp = 0;
let pendingRequest: Promise<LookupItem[]> | null = null;

export function useTransactionTypes() {
  const [types, setTypes] = useState<LookupItem[]>(cachedTypes ?? []);
  const [loading, setLoading] = useState<boolean>(!cachedTypes);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isActive = true;

    if (cachedTypes) {
      setTypes(cachedTypes);
      setLoading(false);
    }

    const cacheIsStale = !cachedTypes || (Date.now() - cacheTimestamp) >= CACHE_TTL_MS;

    if (!cacheIsStale && cachedTypes) {
      return () => {
        isActive = false;
      };
    }

    if (!pendingRequest) {
      pendingRequest = lookupsAPI.getTransactionTypes()
        .then((result) => {
          cachedTypes = result;
          cacheTimestamp = Date.now();
          pendingRequest = null;
          return result;
        })
        .catch((requestError) => {
          pendingRequest = null;
          throw requestError;
        });
    }

    pendingRequest
      ?.then((result) => {
        if (!isActive) {
          return;
        }
        setTypes(result);
        setLoading(false);
      })
      .catch((requestError) => {
        if (!isActive) {
          return;
        }
        const normalizedError = requestError instanceof Error
          ? requestError
          : new Error('Failed to load transaction types');
        setError(normalizedError);
        setLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, []);

  return { types, loading, error };
}
